import { createElement, FC, useContext } from 'react';
import { useRecoilValue } from 'recoil';

import SearchFileUploader from '../component/compound/SearchFileUploader';
import { context as DependencyContext } from '../context/dependency';
import { context as StateContext } from '../context/state';
import { editingProjectStateAtom } from '../page/ProjectEditingPage/atoms/editingProjectStateAtom';
import { projectAtom } from '../page/ProjectEditingPage/atoms/projectAtom';
import { useAssetFiles } from '../page/ProjectEditingPage/hooks/useAssetFiles';
import useUpdateProject from '../page/ProjectEditingPage/hooks/useUpdateProject';
import { Meta } from '../types/Meta';
import { Region } from '../types/Region';

type Props = {
  fieldKey: string;
  meta: Meta;
  disabled: boolean;
  className?: string;
};

export const EnhancedSearchFileUploader: FC<Props> = ({ fieldKey, meta, disabled, className }) => {
  const project = useRecoilValue(projectAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const {
    lastAccessedOrganization,
    projectEditing: { isAssetTermAgreed },
  } = useContext(StateContext);
  const {
    editProjectUsecase: { postAssetFormTermAgreements, submitSearchAssetFile },
  } = useContext(DependencyContext);

  const {
    addUploadingAssetFiles,
    updateUploadingAssetFilesProgress,
    addAssetFile,
    removeUploadingAssetFile,
    removeUploadingAssetFilesProgress,
  } = useAssetFiles();
  const { updateAssetCategoryValueInDraft } = useUpdateProject();

  if (project == null) {
    return null;
  }

  if (project.bucketId == null) {
    return null;
  }

  const region: Region =
    lastAccessedOrganization != null && lastAccessedOrganization.businessRegion.key
      ? lastAccessedOrganization.businessRegion.key
      : 'gl';

  return (
    <SearchFileUploader
      meta={meta}
      region={region}
      disabled={disabled}
      isAssetTermAgreed={isAssetTermAgreed}
      onRequestSearchAssetFileSubmit={(_, selectedSearchAsset) =>
        submitSearchAssetFile({
          addUploadingAssetFiles,
          updateAssetCategoryValueInDraft: () => updateAssetCategoryValueInDraft(state.assetCategoryValue || null),
          updateUploadingAssetFilesProgress,
          selectedSearchAsset,
          bucketId: project.bucketId!,
          fieldKey,
          handleFileUploadFinished: async (file, fileReference, assetCategoryFieldKey) => {
            await removeUploadingAssetFilesProgress(file, assetCategoryFieldKey);
            await removeUploadingAssetFile(file, assetCategoryFieldKey);
            await addAssetFile(fileReference, assetCategoryFieldKey);
          },
        })
      }
      onAssetFormTermAgreementsConfirm={postAssetFormTermAgreements}
      className={className}
    />
  );
};
